import { ensureSubscriptionActionAllowed } from '../utils/subscriptionActionGuard';
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelection } from "../SelectionContext";
import { useAuth } from "../context/AuthContext";
import {
  Table,
  Button,
  Card,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Tabs,
  message,
  Typography,
  Tag,
  Radio,
  InputNumber,
  Row,
  Col,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  FileExcelOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import StockProductHistory from "./StockProductHistory";
import RowActionsMenu from "../components/RowActionsMenu";
import { SwapOutlined, ClockCircleOutlined, CheckCircleOutlined, PlusCircleOutlined, MinusCircleOutlined,
  HistoryOutlined, SlidersOutlined } from "@ant-design/icons";
import { StockPageHeader, StockSummary, StockReadNotice, StockFilters, StockFilterField, StockRemoteSelect,
  useStockWorkspaceTheme, useStockListRefresh, dateParams } from "./StockWorkspace";
import {
  buildStockAdjustmentProductOptions,
  matchesStockAdjustmentOption,
} from "./stockAdjustmentProducts";
import {
  buildAdjustmentBatchPayload,
  buildAdjustmentDraftItems,
} from "./stockAdjustmentBatch";
import StockReplenishmentReturnModal from "../components/StockReplenishmentReturnModal";
import { isReturnableStockReplenishment } from "../utils/stockReplenishment";
import { discardSalesDraftFromReplenishment } from "../utils/salesReplenishmentDraft";
import useStockFormResources, { stockQuantity } from "./useStockFormResources";
import { StockAdjustmentFormModal, StockTransferFormModal, ProductIdentity, unitOf } from "./StockFormModals";
import StockAdjustmentValuation from "./StockAdjustmentValuation";
import { STOCK_ADJUSTMENT_REASONS, adjustmentReasonLabel, hasAdjustmentValuation, STOCK_ADJUSTMENT_COST_LABELS } from "../utils/stockAdjustmentValuation";

const { Text } = Typography;
const { TextArea } = Input;

export const STOCK_HISTORY_PERMISSIONS = [
  "Gestion Commerciale.Stock.GestionStock.History.view",
];

const hasAnyPermission = (hasPermission, permissions = []) =>
  permissions.some((permission) => hasPermission(permission));

/**
 * Main Stock Management component with up to three tabs:
 * 1. Stock Transfers - For transferring products between warehouses
 * 2. Stock Adjustments - For adjusting stock quantities (add/remove)
 * 3. Product History - For consolidated lifecycle events
 */
const GestionStock = () => {
  const workspaceTheme = useStockWorkspaceTheme();
  const [activeTab, setActiveTab] = useState("transfers");
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedWarehouse, selectedCompany } = useSelection();
  const { hasPermission } = useAuth();
  const searchParams = new URLSearchParams(location.search);
  const requestedTab = searchParams.get("tab");
  const requestedProductId = Number(searchParams.get("product_id") || 0) || null;
  const replenishmentPrefill = location.state?.stockReplenishment || null;
  const shouldAutoOpenAdjustment =
    searchParams.get("create") === "1" || Boolean(replenishmentPrefill);
  const initialAdjustmentItems = Array.isArray(replenishmentPrefill?.items)
    ? replenishmentPrefill.items
    : [];
  const [completedReplenishment, setCompletedReplenishment] = useState(null);
  const clearReplenishmentPrefill = useCallback(() => {
    if (!replenishmentPrefill) return;
    discardSalesDraftFromReplenishment(replenishmentPrefill);
    const nextState = { ...(location.state || {}) };
    delete nextState.stockReplenishment;
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      {
        replace: true,
        state: Object.keys(nextState).length ? nextState : null,
      }
    );
  }, [
    location.hash,
    location.pathname,
    location.search,
    location.state,
    navigate,
    replenishmentPrefill,
  ]);
  const canViewHistory = hasAnyPermission(
    hasPermission,
    STOCK_HISTORY_PERMISSIONS
  );
  const resolvedActiveTab =
    !selectedWarehouse && canViewHistory ? "history" : activeTab;

  const handleReplenishmentCancelled = useCallback(() => {
    setCompletedReplenishment(null);
    clearReplenishmentPrefill();
  }, [clearReplenishmentPrefill]);

  const handleReplenishmentCompleted = useCallback(() => {
    if (isReturnableStockReplenishment(replenishmentPrefill)) {
      setCompletedReplenishment(replenishmentPrefill);
      return;
    }

    clearReplenishmentPrefill();
  }, [clearReplenishmentPrefill, replenishmentPrefill]);

  const handleReturnAfterReplenishment = useCallback(
    (returnNavigation) => {
      setCompletedReplenishment(null);
      navigate(returnNavigation.path, {
        replace: true,
        state: returnNavigation.state,
      });
    },
    [navigate]
  );

  // Verify selections
  useEffect(() => {
    if (!selectedWarehouse && resolvedActiveTab !== "history") {
      message.warning(
        "Veuillez sélectionner un établissement pour gérer le stock",
        5
      );
    }
  }, [resolvedActiveTab, selectedWarehouse]);

  useEffect(() => {
    if (
      requestedTab &&
      ["transfers", "adjustments", "history"].includes(requestedTab)
    ) {
      setActiveTab(requestedTab);
    }
  }, [requestedTab]);

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  // Filter tab items based on view permissions
  const availableTabs = [];
  if (hasPermission("Gestion Commerciale.Stock.GestionStock.Transfer.view")) {
    availableTabs.push({
      label: <span><SwapOutlined /> Transferts de stock</span>,
      key: "transfers",
      children: (
        <StockTransfers
          key={`${selectedCompany}|${selectedWarehouse}`}
          selectedWarehouse={selectedWarehouse}
          selectedCompany={selectedCompany}
        />
      ),
    });
  }
  if (hasPermission("Gestion Commerciale.Stock.GestionStock.Adjust.view")) {
    availableTabs.push({
      label: <span><SlidersOutlined /> Ajustements de stock</span>,
      key: "adjustments",
      children: (
        <StockAdjustments
          key={`${selectedCompany}|${selectedWarehouse}`}
          selectedWarehouse={selectedWarehouse}
          selectedCompany={selectedCompany}
          initialProductId={requestedProductId}
          autoOpenCreate={shouldAutoOpenAdjustment}
          initialItems={initialAdjustmentItems}
          prefillRequestId={replenishmentPrefill?.request_id || null}
          replenishmentContext={replenishmentPrefill}
          onReplenishmentCompleted={handleReplenishmentCompleted}
          onReplenishmentCancelled={handleReplenishmentCancelled}
        />
      ),
    });
  }
  if (canViewHistory) {
    availableTabs.push({
      label: <span><HistoryOutlined /> Historique produits</span>,
      key: "history",
      children: (
        <StockProductHistory
          key={`${selectedCompany}|${selectedWarehouse}`}
          selectedWarehouse={selectedWarehouse}
          selectedCompany={selectedCompany}
        />
      ),
    });
  }

  return (
    <div className="stock-workspace" style={workspaceTheme} data-stock-theme={workspaceTheme["--stock-mode"]}>

      {/* Render Tabs only if there are available tabs */}
      {availableTabs.length > 0 ? (
        <div>
          <Tabs
            activeKey={
              availableTabs.some((tab) => tab.key === activeTab)
                ? resolvedActiveTab
                : availableTabs[0]?.key || ""
            }
            onChange={handleTabChange}
            items={availableTabs}
            destroyInactiveTabPane
            className="stock-module-tabs"
          />
        </div>
      ) : (
        <Card>
          <Text type="secondary">
            {selectedWarehouse
              ? "Vous n'avez pas la permission de voir les transferts, les ajustements ou l'historique pour cet établissement."
              : "Veuillez sélectionner un établissement."}
          </Text>
        </Card>
      )}
      <StockReplenishmentReturnModal
        open={Boolean(completedReplenishment)}
        replenishment={completedReplenishment}
        method="adjustment"
        onStay={handleReplenishmentCancelled}
        onReturn={handleReturnAfterReplenishment}
      />
    </div>
  );
};

/**
 * Helper function to format numbers
 * @param {number | string | undefined | null} value The number to format
 * @returns {string} Formatted number string or '0'
 */
const formatNumber = (value) => {
  const num = Number(value);
  if (isNaN(num)) return "0";
  return new Intl.NumberFormat("fr-FR").format(num);
};

const getPaginationTotal = (data) =>
  data?.total ?? data?.pagination?.totalItems ?? data?.pagination?.total ?? 0;

/**
 * Stock Transfers Component - Handles transfers between warehouses
 */
const StockTransfers = ({ selectedWarehouse, selectedCompany }) => {
  const workspaceTheme = useStockWorkspaceTheme();
  const [dialogApi, dialogHolder] = Modal.useModal();
  const [transferDraft, setTransferDraft] = useState({});
  const [transferFilters, setTransferFilters] = useState({});
  const [transferSummary, setTransferSummary] = useState(null);
  const [listError, setListError] = useState("");
  const [loading, setLoading] = useState(false);
  const [transfers, setTransfers] = useState([]);
  const [totalTransfers, setTotalTransfers] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTransferId, setEditingTransferId] = useState(null);
  const [viewMode, setViewMode] = useState("sent"); // 'sent' or 'received'
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [form] = Form.useForm();

  // State for the new item management
  const [transferItems, setTransferItems] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [productSearchKey, setProductSearchKey] = useState(Date.now());
  const { user, hasPermission } = useAuth();
  const transferRequestIdRef = useRef(0);
  const sourceWarehouseId = Form.useWatch("from_warehouse_id", form);
  const [productSearch, setProductSearch] = useState("");
  const [settledSearch, setSettledSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setSettledSearch(productSearch), 500);
    return () => clearTimeout(timer);
  }, [productSearch]);
  const resources = useStockFormResources({ kind: "transfer", open: modalVisible,
    userId: user?.id, companyId: selectedCompany, warehouseId: sourceWarehouseId || selectedWarehouse,
    search: settledSearch, selectedIds: transferItems.map(item => item.product_id) });
  const productOptions = (resources.products.data || []).map(product => ({
    value: product.id, product, label: `${product.name || product.nom_produit} (Stock : ${stockQuantity(product) ?? "indisponible"})`,
  }));
  const context = `${user?.id}|${selectedCompany}|${selectedWarehouse}`;
  const contextRef = useRef(context);
  const previousSourceRef = useRef(sourceWarehouseId);
  const formRequestRef = useRef(0);
  const submittingRef = useRef(false);
  const itemsRef = useRef(transferItems);
  itemsRef.current = transferItems;
  contextRef.current = context;
  const transferRows = transferItems.map(item => {
    const product = resources.selected.data?.find(row => String(row.id) === String(item.product_id));
    const stock = product ? stockQuantity(product) : null;
    const available = stock === null ? null : stock + (editingTransferId ? Number(item.original_quantity || 0) : 0);
    return { ...item, image: product?.image, unit_name: product?.unit_name, unit_short_name: product?.unit_short_name,
      current_stock: stock, available_stock: available,
      conflict: !product || available === null || !(Number(item.quantity) > 0) || Number(item.quantity) > available ||
        item.transfer_price === null || !Number.isFinite(Number(item.transfer_price)) || Number(item.transfer_price) < 0 };
  });
  const closeTransferModal = () => {
    if (submittingRef.current) return;
    formRequestRef.current += 1;
    setModalVisible(false);
  };

  // Fetch transfers based on selected warehouse and view mode
  const fetchTransfers = useCallback(async () => {
    if (!selectedWarehouse) return;

    const requestId = ++transferRequestIdRef.current;
    const requestedContext = context;
    setLoading(true);
    try {
      const res = await axios.get("/api/orders", {
        headers: { "X-Company-Id": String(selectedCompany) },
        params: {
          order_type: "stock-transfer",
          warehouse: selectedWarehouse,
          view_type: viewMode,
          page: currentPage,
          limit: pageSize,
          include_stock_summary: 1,
          invoice_number: transferFilters.search || undefined,
          product_id: transferFilters.product,
          counterpart_id: transferFilters.counterpart,
          order_status: transferFilters.status,
          ...dateParams(transferFilters.period, "dateDebut", "dateFin"),
        },
      });

      if (requestId !== transferRequestIdRef.current || requestedContext !== contextRef.current) return;
      setTransfers(res.data.orders || []);
      setTotalTransfers(getPaginationTotal(res.data));
      setTransferSummary(res.data.stock_summary || null);
      setListError("");
    } catch (error) {
      if (requestId !== transferRequestIdRef.current) return;
      setListError("Impossible d’actualiser les transferts de stock.");
    } finally {
      if (requestId === transferRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [selectedWarehouse, selectedCompany, context, viewMode, currentPage, pageSize, transferFilters]);
  useStockListRefresh(fetchTransfers);

  useEffect(() => {
    formRequestRef.current += 1;
    transferRequestIdRef.current += 1;
    setModalVisible(false);
    setLoading(false);
    setCurrentPage(1);
    setSelectedRowKeys([]);
    setTransferItems([]);
    setProductSearch("");
    setTransfers([]);
    setTotalTransfers(0);
    setTransferDraft({}); setTransferFilters({}); setTransferSummary(null); setListError("");
    form.resetFields();
    return () => { formRequestRef.current += 1; transferRequestIdRef.current += 1; };
  }, [user?.id, selectedCompany, selectedWarehouse, form]);

  // ✅ FIX: Recharger les transferts quand viewMode, currentPage ou pageSize changent
  useEffect(() => {
    if (selectedWarehouse) {
      fetchTransfers();
    }
  }, [selectedWarehouse, fetchTransfers]);

  const handleViewModeChange = (nextViewMode) => {
    setCurrentPage(1);
    setSelectedRowKeys([]);
    setViewMode(nextViewMode);
    setTransfers([]); setTransferSummary(null); setTotalTransfers(0); setListError("");
  };



  // Function to update calculations for a specific item (simplified)
  const calculateItemTotals = (item) => {
    if (!item) return item;

    const quantity = Number(item.quantity) || 0;
    const transferPrice = Number(item.transfer_price) || 0;
    const total = quantity * transferPrice;

    return {
      ...item,
      total: total, // Only total per line now
    };
  };

  useEffect(() => {
    let newGrandTotal = 0;
    transferItems.forEach((item) => {
      newGrandTotal += Number(item?.total) || 0;
    });
    setGrandTotal(newGrandTotal);
  }, [transferItems]);

  // Les détails d'édition sont isolés de la recherche et protégés des réponses tardives.
  const openTransferModal = async (transferId = null) => {
    const permission = `Gestion Commerciale.Stock.GestionStock.Transfer.${transferId ? "edit" : "create"}`;
    if (!hasPermission(permission)) return;
    const request = ++formRequestRef.current;
    const openedContext = contextRef.current;
    setEditingTransferId(transferId);
    setTransferItems([]);
    setProductSearch("");
    form.resetFields();
    previousSourceRef.current = selectedWarehouse;
    form.setFieldsValue({ date: dayjs(), from_warehouse_id: selectedWarehouse });
    setModalVisible(true);
    if (!transferId) return;
    setLoading(true);
    try {
      const options = { headers: { "X-Company-Id": String(selectedCompany) } };
      const [details, lines] = await Promise.all([
        axios.get(`/api/orders/${transferId}`, options), axios.get(`/api/orders/${transferId}/items`, options),
      ]);
      if (request !== formRequestRef.current || openedContext !== contextRef.current) return;
      const data = details.data;
      previousSourceRef.current = data.from_warehouse_id;
      form.setFieldsValue({ date: dayjs(data.order_date), from_warehouse_id: data.from_warehouse_id,
        to_warehouse_id: data.warehouse_id, remarks: data.notes });
      setTransferItems(lines.data.map(item => calculateItemTotals({
        key: item.id || item.product_id, product_id: item.product_id,
        product_name: item.product_name || item.name || `Produit ${item.product_id}`,
        quantity: Number(item.quantity), original_quantity: Number(item.quantity), transfer_price: Number(item.unit_price),
      })));
    } catch (error) {
      if (request === formRequestRef.current && openedContext === contextRef.current) {
        message.error("Impossible de charger les détails du transfert");
        setModalVisible(false);
      }
    } finally {
      if (request === formRequestRef.current) setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (values) => {
    if (transferItems.length === 0) {
      message.warning(
        "Veuillez ajouter au moins un produit valide au transfert."
      );
      return;
    }
    if (submittingRef.current || !hasPermission(`Gestion Commerciale.Stock.GestionStock.Transfer.${editingTransferId ? "edit" : "create"}`)) return;
    submittingRef.current = true;
    const submittedContext = contextRef.current;
    const request = formRequestRef.current;
    const snapshot = JSON.stringify({ values: form.getFieldsValue(true), items: itemsRef.current });
    try {
      setLoading(true);
      const fresh = await resources.refresh();
      if (submittedContext !== contextRef.current || request !== formRequestRef.current ||
        snapshot !== JSON.stringify({ values: form.getFieldsValue(true), items: itemsRef.current })) {
        throw new Error("Le contexte ou la saisie a changé. Vérifiez le transfert avant de réessayer.");
      }
      if (![values.from_warehouse_id, values.to_warehouse_id].every(id => fresh.warehouses.some(row => String(row.id) === String(id)))) {
        throw new Error("Un établissement sélectionné n’est plus disponible.");
      }
      for (const item of transferItems) {
        const product = fresh.products.find(row => String(row.id) === String(item.product_id));
        const stock = stockQuantity(product);
        const available = stock === null ? null : stock + (editingTransferId ? Number(item.original_quantity || 0) : 0);
        if (!product || available === null || !(Number(item.quantity) > 0) || Number(item.quantity) > available ||
          item.transfer_price === null || !Number.isFinite(Number(item.transfer_price)) || Number(item.transfer_price) < 0) {
          throw new Error(`Vérifiez le stock, la quantité et le prix de « ${item.product_name} ». La saisie est conservée.`);
        }
      }
      const formData = {
        company_id: selectedCompany,
        from_warehouse_id: values.from_warehouse_id,
        warehouse_id: values.to_warehouse_id,
        order_date: values.date.format("YYYY-MM-DD"),
        order_type: "stock-transfer",
        notes: values.remarks,
        order_status: "completed",
        payment_status: "n/a",
        items: transferItems.map((item) => {
          return {
            product_id: item.product_id,
            quantity: Number(item.quantity) || 0,
            unit_price: Number(item.transfer_price) || 0,
            subtotal: Number(item.total) || 0,
            discount_rate: 0,
          };
        }),
      };

      formData.subtotal = formData.items.reduce(
        (sum, item) => sum + item.subtotal,
        0
      );
      formData.total = formData.subtotal;

      formData.total_items = formData.items.length;
      formData.total_quantity = formData.items.reduce(
        (sum, item) => sum + Number(item.quantity || 0),
        0
      );


      if (editingTransferId) {
        await axios.put(
          `/api/orders/${editingTransferId}`,
          formData
        );
        message.success("Transfert mis à jour avec succès");
      } else {
        await axios.post("/api/orders", formData);
        message.success("Transfert créé avec succès");
      }

      void resources.invalidate();
      if (submittedContext === contextRef.current) {
        setModalVisible(false);
        fetchTransfers();
      }
    } catch (error) {
      message.error(
        `Erreur lors de l'enregistrement du transfert: ${
          error.response?.data?.error || error.message
        }`
      );
    } finally {
      submittingRef.current = false;
      if (submittedContext === contextRef.current) setLoading(false);
    }
  };

  // Handle delete transfer
  const handleDelete = async (transferId) => {
    try {
      setLoading(true);
      await axios.delete(`/api/orders/${transferId}`);
      void resources.invalidate();
      message.success("Transfert supprimé avec succès");
      fetchTransfers();
    } catch (error) {
      // ✅ Afficher le message détaillé du backend avec dialogApi.error()
      if (error.response?.data) {
        const errorData = error.response.data;

        dialogApi.error({
          title: errorData.error || "Suppression impossible",
          content: (
            <div>
              <p style={{ marginBottom: '10px', fontWeight: 'bold' }}>
                {errorData.message}
              </p>
              {errorData.explication && (
                <p style={{ marginBottom: '10px' }}>
                  {errorData.explication}
                </p>
              )}
              {errorData.details && errorData.details.stock_actuel && (
                <div style={{
                  background: '#f5f5f5',
                  padding: '10px',
                  borderRadius: '4px',
                  marginBottom: '10px'
                }}>
                  <strong>Détails :</strong>
                  <ul style={{ marginTop: '5px', marginBottom: '0' }}>
                    {errorData.details.produit && <li>Produit : {errorData.details.produit}</li>}
                    <li>Stock actuel : {errorData.details.stock_actuel}</li>
                    <li>Opération : {errorData.details.ajustement || errorData.details.operation}</li>
                    <li>Résultat : {errorData.details.stock_apres_suppression}</li>
                  </ul>
                </div>
              )}
              {errorData.que_faire && errorData.que_faire.length > 0 && (
                <div>
                  <strong>Que faire ?</strong>
                  <ul style={{ marginTop: '5px', marginBottom: '0', paddingLeft: '20px' }}>
                    {errorData.que_faire.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ),
          width: 600,
          okText: "Compris"
        });
      } else {
        message.error("Erreur lors de la suppression du transfert");
      }
    } finally {
      setLoading(false);
    }
  };

  // When product is selected from search, add/update in transferItems state
  const handleProductSelect = (value, option) => {
    if (resources.products.status !== "verified") return;
    if (!option || !option.product) {
      message.error(
        "Impossible de récupérer les détails du produit sélectionné."
      );
      return;
    }
    const selectedProduct = option.product;
    const sourceStock =
      selectedProduct.current_stock ?? selectedProduct.quantite_stock;

    if (sourceStock === undefined || sourceStock <= 0) {
      message.error(
        `Le produit "${
          selectedProduct.name || selectedProduct.nom_produit
        }" n'a pas de stock disponible dans l'établissement source.`
      );
      setProductSearchKey(Date.now());
      setProductSearch("");
      return;
    }

    // ✅ AVERTISSEMENT: Détecter si c'est un transfert secondaire
    const isTransferredProduct = selectedProduct.main_warehouse_nom &&
                                  selectedProduct.main_warehouse_nom !== selectedProduct.warehouse_nom &&
                                  selectedProduct.warehouse_nom !== "N/A";

    if (isTransferredProduct) {
      Modal.warning({
        title: "Transfert secondaire détecté",
        content: (
          <div>
            <p style={{ marginBottom: '10px', fontWeight: 'bold' }}>
              Attention : Ce produit a déjà été transféré depuis "{selectedProduct.main_warehouse_nom}".
            </p>
            <p style={{ marginBottom: '10px' }}>
              Transférer ce produit vers un autre établissement créera une chaîne de transferts difficile à tracer.
            </p>
            <div style={{
              background: '#fff7e6',
              padding: '10px',
              borderRadius: '4px',
              border: '1px solid #ffa940',
              marginBottom: '10px'
            }}>
              <strong>Recommandations :</strong>
              <ul style={{ marginTop: '5px', marginBottom: '0', paddingLeft: '20px' }}>
                <li>Préférez retourner le stock à "{selectedProduct.main_warehouse_nom}" d'abord</li>
                <li>Puis effectuer le transfert depuis l'établissement propriétaire</li>
                <li>Cela garantit une meilleure traçabilité</li>
              </ul>
            </div>
            <p style={{ marginBottom: '0', fontSize: '12px', color: '#666' }}>
              Le transfert reste possible, mais cette pratique est déconseillée.
            </p>
          </div>
        ),
        width: 600,
        okText: "J'ai compris"
      });
    }

    setTransferItems((currentItems) => {
      const existingItemIndex = currentItems.findIndex(
        (item) => item.product_id === selectedProduct.id
      );
      let updatedItems = [...currentItems];

      if (existingItemIndex !== -1) {
        const currentQuantity = updatedItems[existingItemIndex].quantity;
        if (sourceStock !== undefined && currentQuantity + 1 > sourceStock) {
          message.warning(
            `La quantité demandée dépasse le stock disponible (${sourceStock}).`
          );
        } else {
          updatedItems[existingItemIndex] = calculateItemTotals({
            ...updatedItems[existingItemIndex],
            quantity: currentQuantity + 1,
          });
          message.success(
            `Quantité de ${
              selectedProduct.name || selectedProduct.nom_produit
            } augmentée.`
          );
        }
      } else {
        const newItem = calculateItemTotals({
          key: selectedProduct.id,
          product_id: selectedProduct.id,
          product_name: selectedProduct.name || selectedProduct.nom_produit,
          current_stock: sourceStock,
          unit_name: selectedProduct.unit_name,
          unit_short_name: selectedProduct.unit_short_name,
          quantity: 1,
          transfer_price:
            selectedProduct.cost_price ?? // Prioritize cost price
            selectedProduct.sales_price ??
            selectedProduct.price ??
            0,
        });
        updatedItems.push(newItem);
        message.success(
          `${
            selectedProduct.name || selectedProduct.nom_produit
          } ajouté au transfert.`
        );
      }
      return updatedItems;
    });
    setProductSearchKey(Date.now());
    setProductSearch("");
  };

  // Un conflit de stock conserve la quantité saisie et bloque l'enregistrement.
  const handleItemChange = (key, field, value) => {
    setTransferItems(current => current.map(item => item.key === key
      ? calculateItemTotals({ ...item, [field]: value }) : item));
  };

  // Handle item deletion from the table
  const handleDeleteItem = (key) => {
    setTransferItems((currentItems) =>
      currentItems.filter((item) => item.key !== key)
    );
  };

  // Define columns for transfers list table
  const columns = [
    {
      title: "N° du transfert",
      dataIndex: "invoice_number",
      key: "invoice_number",
      render: value => <span className="stock-identity">{value}</span>,
    },
    {
      title: "Date",
      dataIndex: "order_date",
      key: "order_date",
      render: (date) => moment(date).format("DD/MM/YYYY"),
    },
    {
      title: viewMode === "sent" ? "Destination" : "Source",
      dataIndex: viewMode === "sent" ? "warehouse_name" : "from_warehouse_name",
      key: "warehouse",
    },
    {
      title: "Statut",
      dataIndex: "order_status",
      key: "order_status",
      render: (status) => {
        // Map status to display
        const statusMap = {
          'in_transit': { label: 'En transit', color: 'gold' },
          'received': { label: 'Reçu', color: 'green' },
          'reçu': { label: 'Reçu', color: 'green' },
          'recu': { label: 'Reçu', color: 'green' },
          'completed': { label: 'Terminé', color: 'green' },
          'terminé': { label: 'Terminé', color: 'green' },
          'termine': { label: 'Terminé', color: 'green' }
        };

        const statusDisplay = statusMap[String(status).toLowerCase().trim()] || { label: status, color: 'blue' };

        return (
          <Tag color={statusDisplay.color} icon={statusDisplay.color === "gold" ? <ClockCircleOutlined /> : statusDisplay.color === "green" ? <CheckCircleOutlined /> : undefined}>
            {statusDisplay.label}
          </Tag>
        );
      },
    },
    {
      title: "Produits",
      dataIndex: "total_items",
      key: "total_items",
      align: "right",
      render: (value, record) => value ?? record.items?.length ?? "—",
    },
    {
      title: "Quantité",
      dataIndex: "total_quantity",
      key: "total_quantity",
      align: "right",
      render: (value, record) => formatNumber(value ?? record.items?.reduce((sum, item) => sum + Number(item.quantity || 0), 0)),
    },
    {
      title: "Actions", key: "actions", width: 80,
      render: (_, record) => <RowActionsMenu ariaLabel={`Actions du transfert ${record.invoice_number || record.id}`} items={[
        hasPermission("Gestion Commerciale.Stock.GestionStock.Transfer.edit") && { key: "edit", label: "Modifier", icon: <EditOutlined />,
          disabled: viewMode === "received", onClick: () => openTransferModal(record.id) },
        hasPermission("Gestion Commerciale.Stock.GestionStock.Transfer.delete") && { key: "delete", label: "Supprimer", danger: true, icon: <DeleteOutlined />,
          disabled: viewMode === "received", onClick: () => dialogApi.confirm({ title: "Supprimer ce transfert ?", content: record.invoice_number,
            okText: "Supprimer", cancelText: "Annuler", okButtonProps: { danger: true }, onOk: () => handleDelete(record.id) }) },
      ].filter(Boolean)} />,
    },
  ];

  const itemTableColumns = [
    { title: "Produit", key: "product", width: 220, render: (_, row) => <><ProductIdentity row={row} />
      {row.conflict && <small className="stock-form-error">Vérifiez le stock et la quantité.</small>}</> },
    { title: "Stock source", key: "stock", width: 100, render: (_, row) => row.current_stock === null ? "Indisponible" : `${formatNumber(row.current_stock)} ${unitOf(row)}` },
    { title: "Quantité", key: "quantity", width: 140, render: (_, row) => <InputNumber min={1} value={row.quantity}
      aria-label={`Quantité pour ${row.product_name}`} status={row.conflict ? "error" : undefined}
      addonAfter={unitOf(row) || undefined} onChange={value => handleItemChange(row.key, "quantity", value)} /> },
    { title: "Prix unitaire (CFA)", key: "price", width: 150, render: (_, row) => <InputNumber min={0} value={row.transfer_price}
      aria-label={`Prix pour ${row.product_name}`} onChange={value => handleItemChange(row.key, "transfer_price", value)} /> },
    { title: "Total ligne (CFA)", key: "total", align: "right", width: 140, render: (_, row) => formatNumber(row.total) },
    { title: "Action", key: "action", width: 70, align: "center", render: (_, row) => <Button type="text" danger icon={<DeleteOutlined />}
      aria-label={`Retirer ${row.product_name}`} onClick={() => handleDeleteItem(row.key)} /> },
  ];
  useEffect(() => {
    if (modalVisible && String(previousSourceRef.current) !== String(sourceWarehouseId)) {
      setTransferItems([]);
      setProductSearch("");
      setSettledSearch("");
    }
    previousSourceRef.current = sourceWarehouseId;
  }, [sourceWarehouseId, modalVisible]);

  // Handle multiple delete
  const handleDeleteMultiple = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning(
        "Veuillez sélectionner au moins un transfert à supprimer."
      );
      return;
    }

    dialogApi.confirm({
      title: `Êtes-vous sûr de vouloir supprimer ${selectedRowKeys.length} transfert(s) ?`,
      content: "Cette action est irréversible et le stock sera restauré.",
      okText: "Oui, Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        setLoading(true);
        try {
          // Create an array of delete promises
          const deletePromises = selectedRowKeys.map((id) =>
            axios.delete(`/api/orders/${id}`)
          );
          // Wait for all delete requests to complete
          await Promise.all(deletePromises);

          message.success(
            `${selectedRowKeys.length} transfert(s) supprimé(s) avec succès.`
          );
          setSelectedRowKeys([]); // Clear selection
          fetchTransfers(); // Refresh the list
        } catch (error) {
          message.error(
            "Erreur lors de la suppression des transferts sélectionnés."
          );
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Row selection config
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => {
      setSelectedRowKeys(selectedKeys);
    },
  };

  const hasSelected = selectedRowKeys.length > 0;

  return (
    <div className="stock-workspace-pane" style={workspaceTheme} data-stock-theme={workspaceTheme["--stock-mode"]}>
      {dialogHolder}
      <StockPageHeader description="Suivez les transferts entre vos établissements.">
        {hasPermission("Gestion Commerciale.Stock.GestionStock.Transfer.create") && <Button type="primary" icon={<PlusOutlined />}
          onClick={() => openTransferModal()} disabled={!selectedWarehouse}>Nouveau transfert</Button>}
      </StockPageHeader>
      <StockSummary loading={loading} unavailable={!selectedWarehouse} items={[
        { label: "Total des transferts", value: transferSummary?.total, icon: <SwapOutlined /> },
        { label: "En attente de réception", value: transferSummary?.pending, icon: <ClockCircleOutlined />, tone: "warning", help: "Transferts en transit" },
        { label: "Terminés", value: transferSummary?.completed, icon: <CheckCircleOutlined />, tone: "success" },
      ]} />
      <StockReadNotice error={listError} retry={fetchTransfers} loading={loading} hasData={Boolean(transferSummary || transfers.length)} />
      <section className="stock-register">
        <div className="stock-register-heading"><h2>Transferts</h2>
          <Radio.Group value={viewMode} onChange={event => handleViewModeChange(event.target.value)} buttonStyle="solid">
            <Radio.Button value="sent"><ArrowRightOutlined /> Envoyés</Radio.Button>
            <Radio.Button value="received"><ArrowLeftOutlined /> Reçus</Radio.Button>
          </Radio.Group>
        </div>
        <StockFilters searchLabel="Rechercher un transfert" placeholder="N° du transfert…" value={transferDraft} onChange={setTransferDraft}
          onApply={() => { setTransferFilters(transferDraft); setCurrentPage(1); setSelectedRowKeys([]); }}
          onReset={() => { setTransferDraft({}); setTransferFilters({}); setCurrentPage(1); setSelectedRowKeys([]); }}>
          <StockRemoteSelect endpoint="/api/orders" params={{ order_type: "stock-transfer", warehouse: selectedWarehouse, view_type: viewMode }}
            companyId={selectedCompany} warehouseId={selectedWarehouse} kind="warehouses" label={viewMode === "sent" ? "Destination" : "Source"}
            value={transferDraft.counterpart} placeholder="Tous les établissements" onChange={counterpart => setTransferDraft({ ...transferDraft, counterpart })} />
          <StockFilterField label="Statut"><Select aria-label="Statut" allowClear value={transferDraft.status} placeholder="Tous les statuts"
            options={[{ value: "in_transit", label: "En transit" }, { value: "received", label: "Reçu" }, { value: "completed", label: "Terminé" }]}
            onChange={status => setTransferDraft({ ...transferDraft, status })} /></StockFilterField>
          <StockRemoteSelect endpoint="/api/orders" params={{ order_type: "stock-transfer", warehouse: selectedWarehouse, view_type: viewMode }}
            companyId={selectedCompany} warehouseId={selectedWarehouse} kind="products" label="Produit" value={transferDraft.product}
            placeholder="Tous les produits" onChange={product => setTransferDraft({ ...transferDraft, product })} />
        </StockFilters>
        {hasSelected && hasPermission("Gestion Commerciale.Stock.GestionStock.Transfer.delete") && <div className="stock-bulk-actions">
          <span>{selectedRowKeys.length} transfert(s) sélectionné(s)</span><Button danger icon={<DeleteOutlined />} onClick={handleDeleteMultiple}
            disabled={viewMode === "received"} loading={loading}>Supprimer la sélection</Button></div>}
        <Table className="stock-workspace-table" rowSelection={rowSelection} dataSource={transfers} columns={columns} rowKey="id" loading={loading}
          scroll={{ x: 980 }} locale={{ emptyText: listError ? "Données indisponibles." : selectedWarehouse ? "Aucun transfert pour ces filtres." : "Sélectionnez un établissement." }}
          expandable={{ expandedRowRender: record => <Table size="small" pagination={false} dataSource={record.items || []} rowKey="id" columns={[
            { title: "Produit", dataIndex: "product_name" },
            { title: "Quantité", align: "right", render: (_, item) => `${formatNumber(item.quantity)} ${item.unit_short_name || item.unit_name || ""}` },
            { title: "Prix unitaire", dataIndex: "unit_price", align: "right", render: value => `${formatNumber(value)} CFA` },
            { title: "Total", dataIndex: "subtotal", align: "right", render: value => `${formatNumber(value)} CFA` },
          ]} />, rowExpandable: record => Boolean(record.items?.length) }}
          pagination={{ current: currentPage, pageSize, total: totalTransfers, showSizeChanger: true, onChange: (page, size) => { setCurrentPage(page); setPageSize(size); },
            showTotal: (total, range) => `${range[0]}–${range[1]} sur ${total} transferts` }} />
      </section>

      <StockTransferFormModal open={modalVisible} form={form} onSubmit={handleSubmit}
        onCancel={closeTransferModal} loading={loading} editing={Boolean(editingTransferId)}
        selectedWarehouse={selectedWarehouse} items={transferRows} columns={itemTableColumns}
        resources={resources} productOptions={productOptions} onSearch={setProductSearch}
        onAdd={handleProductSelect} searchKey={productSearchKey} total={grandTotal}
        canSubmit={transferItems.length > 0 && hasPermission(`Gestion Commerciale.Stock.GestionStock.Transfer.${editingTransferId ? "edit" : "create"}`)}
        conflict={transferRows.some(row => row.conflict)} />
    </div>
  );
};

/**
 * Stock Adjustments Component - Handles adding/removing stock quantities
 */
const StockAdjustments = ({
  selectedWarehouse,
  selectedCompany,
  initialProductId = null,
  autoOpenCreate = false,
  initialItems = [],
  prefillRequestId = null,
  replenishmentContext = null,
  onReplenishmentCompleted,
  onReplenishmentCancelled,
}) => {
  const workspaceTheme = useStockWorkspaceTheme();
  const [dialogApi, dialogHolder] = Modal.useModal();
  const [adjustmentDraft, setAdjustmentDraft] = useState({ product: initialProductId || undefined });
  const [adjustmentFilters, setAdjustmentFilters] = useState({});
  const [adjustmentSort, setAdjustmentSort] = useState({ field: "created_at", order: "descend" });
  const [adjustmentSummary, setAdjustmentSummary] = useState(null);
  const [listError, setListError] = useState("");
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [adjustments, setAdjustments] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAdjustmentId, setEditingAdjustmentId] = useState(null);
  const [editingValuation, setEditingValuation] = useState(null);
  const [productFilter, setProductFilter] = useState(null);
  const [dateRangeFilter, setDateRangeFilter] = useState(null);
  const [draftItems, setDraftItems] = useState([]);
  const [form] = Form.useForm();
  const { user, hasPermission } = useAuth();
  const isMountedRef = useRef(true);
  const adjustmentsRequestIdRef = useRef(0);
  const autoOpenHandledRef = useRef(false);
  const resources = useStockFormResources({ kind: "adjustment", open: modalVisible,
    userId: user?.id, companyId: selectedCompany, warehouseId: selectedWarehouse });
  const products = useMemo(() => resources.products.data || [], [resources.products.data]);
  const productsLoading = resources.products.loading;
  const formRequestRef = useRef(0);
  const submittingRef = useRef(false);
  const context = `${user?.id}|${selectedCompany}|${selectedWarehouse}`;
  const contextRef = useRef(context);
  const itemsRef = useRef(draftItems);
  contextRef.current = context;
  itemsRef.current = draftItems;
  const productOptions = useMemo(
    () => buildStockAdjustmentProductOptions(products),
    [products]
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; formRequestRef.current += 1; };
  }, []);
  useEffect(() => {
    formRequestRef.current += 1;
    adjustmentsRequestIdRef.current += 1;
    setModalVisible(false);
    setDraftItems([]);
    setLoading(false);
    setAdjustments([]); setTotalItems(0); setCurrentPage(1); setAdjustmentSummary(null); setListError("");
    setProductFilter(initialProductId || null); setDateRangeFilter(null);
    setAdjustmentDraft({ product: initialProductId || undefined }); setAdjustmentFilters({});
    form.resetFields();
  }, [user?.id, selectedCompany, selectedWarehouse, form, initialProductId]);

  useEffect(() => {
    if (initialProductId) {
      setProductFilter(initialProductId);
      setAdjustmentDraft(draft => ({ ...draft, product: initialProductId }));
      setCurrentPage(1);
    }
  }, [initialProductId]);

  useEffect(() => {
    autoOpenHandledRef.current = false;
  }, [
    initialProductId,
    autoOpenCreate,
    prefillRequestId,
    selectedWarehouse,
  ]);

  const buildAdjustmentParams = useCallback(
    (overrides = {}) => {
      const params = {
        warehouse_id: selectedWarehouse,
        page: currentPage,
        limit: pageSize,
        sort_by: adjustmentSort.field,
        order: adjustmentSort.order === "ascend" ? "asc" : "desc",
        search: adjustmentFilters.search || undefined,
        adjustment_type: adjustmentFilters.type,
        actor_id: adjustmentFilters.actor,
        ...overrides,
      };

      if (productFilter) {
        params.product_id = productFilter;
      }

      if (dateRangeFilter && dateRangeFilter.length === 2) {
        params.start_date = dateRangeFilter[0].format("YYYY-MM-DD");
        params.end_date = dateRangeFilter[1].format("YYYY-MM-DD");
      }

      return params;
    },
    [selectedWarehouse, currentPage, pageSize, productFilter, dateRangeFilter, adjustmentFilters, adjustmentSort]
  );

  // Fetch adjustments based on selected warehouse and filters
  const fetchAdjustments = useCallback(async () => {
    const requestId = adjustmentsRequestIdRef.current + 1;
    adjustmentsRequestIdRef.current = requestId;
    const isStaleRequest = () =>
      !isMountedRef.current || requestId !== adjustmentsRequestIdRef.current;

    if (!selectedWarehouse) return;

    if (isStaleRequest()) return;
    setLoading(true);
    try {
      const params = buildAdjustmentParams({ include_summary: 1 });
      const requestedContext = context;
      const res = await axios.get("/api/stock-adjustments", { params, headers: { "X-Company-Id": String(selectedCompany) } });
      if (requestedContext !== contextRef.current) return;
      if (isStaleRequest()) return;

      setAdjustments(res.data.adjustments || []);
      setTotalItems(getPaginationTotal(res.data));
      setAdjustmentSummary(res.data.summary || null); setListError("");
    } catch (error) {
      if (!isStaleRequest()) {
        setListError("Impossible d’actualiser les ajustements de stock.");
      }
    } finally {
      if (!isStaleRequest()) {
        setLoading(false);
      }
    }
  }, [
    selectedWarehouse,
    selectedCompany,
    context,
    buildAdjustmentParams,
  ]);
  useStockListRefresh(fetchAdjustments);

  useEffect(() => {
    if (selectedWarehouse) {
      fetchAdjustments();
    }
  }, [selectedWarehouse, fetchAdjustments]);

  const handleExportToExcel = async () => {
    if (!(await ensureSubscriptionActionAllowed('data_export'))) return;
    if (!hasPermission("Gestion Commerciale.Stock.GestionStock.Adjust.view")) {
      message.error(
        "Vous n'avez pas la permission d'exporter les ajustements de stock."
      );
      return;
    }

    if (!selectedWarehouse) {
      message.warning("Veuillez sélectionner un établissement avant l'export.");
      return;
    }

    const exportContext = context;
    try {
      setExportLoading(true);
      message.loading({
        content: "Préparation de l'export Excel...",
        key: "stockAdjustmentsExport",
      });

      const filtersApplied = [`Établissement: ID ${selectedWarehouse}`];
      const selectedProduct = products.find(
        (product) => String(product.id) === String(productFilter)
      );

      if (selectedProduct) {
        filtersApplied.push(
          `Produit: ${selectedProduct.name || selectedProduct.nom_produit}`
        );
      }

      if (dateRangeFilter && dateRangeFilter.length === 2) {
        filtersApplied.push(
          `Période: du ${dateRangeFilter[0].format("YYYY-MM-DD")} au ${dateRangeFilter[1].format("YYYY-MM-DD")}`
        );
      }

      const pageSizeForExport = 500;
      let pageToLoad = 1;
      let totalToExport = null;
      let adjustmentsToExport = [];

      while (
        totalToExport === null ||
        adjustmentsToExport.length < totalToExport
      ) {
        const params = buildAdjustmentParams({
          page: pageToLoad,
          limit: pageSizeForExport,
        });
        const res = await axios.get("/api/stock-adjustments", { params, headers: { "X-Company-Id": String(selectedCompany) } });
        if (exportContext !== contextRef.current) throw new Error("Le contexte a changé. Relancez l’export.");
        const batch = res.data?.adjustments || [];

        if (totalToExport === null) {
          totalToExport = getPaginationTotal(res.data);
        }

        if (batch.length === 0) {
          break;
        }

        adjustmentsToExport = adjustmentsToExport.concat(batch);

        if (batch.length < pageSizeForExport) {
          break;
        }

        pageToLoad += 1;
      }

      if (adjustmentsToExport.length === 0) {
        message.warning({
          content: "Aucun ajustement trouvé avec les filtres appliqués.",
          key: "stockAdjustmentsExport",
        });
        return;
      }

      const dataToExport = adjustmentsToExport.map((adjustment) => ({
        Date: adjustment.created_at
          ? dayjs(adjustment.created_at).format("DD/MM/YYYY HH:mm")
          : "-",
        Produit: adjustment.product_name || "Produit inconnu",
        Unite: adjustment.unit_short_name || adjustment.unit_name || "-",
        Type: adjustment.adjustment_type === "add" ? "Ajout" : "Retrait",
        Quantité: `${
          adjustment.adjustment_type === "add" ? "+" : "-"
        }${formatNumber(adjustment.quantity)}`,
        Motif: adjustmentReasonLabel(adjustment.reason_code),
        "Coût unitaire enregistré": hasAdjustmentValuation(adjustment) ? Number(adjustment.unit_cost_snapshot) : "",
        "Valeur du stock (+/−)": hasAdjustmentValuation(adjustment) ? Number(adjustment.stock_value_delta) : "",
        Devise: adjustment.valuation_currency || "",
        "Source du coût": STOCK_ADJUSTMENT_COST_LABELS[adjustment.valuation_source] || "Non valorisé",
        "Date de valorisation": adjustment.valued_at ? dayjs(adjustment.valued_at).format("DD/MM/YYYY HH:mm:ss") : "",
        Notes: adjustment.notes || "",
        "Créé par": adjustment.user_name || "Utilisateur supprimé",
        Établissement: adjustment.warehouse_name || `ID ${adjustment.warehouse_id}`,
      }));

      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      worksheet["!cols"] = [
        { wch: 18 },
        { wch: 30 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        ...[24, 24, 24, 10, 38, 24].map(wch => ({ wch })),
        { wch: 40 },
        { wch: 24 },
        { wch: 20 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Ajustements de stock"
      );

      const today = dayjs().format("YYYY-MM-DD");
      const filename = `Ajustements_Stock_${today}.xlsx`;
      XLSX.writeFile(workbook, filename);

      message.success({
        content: (
          <>
            <div>Export Excel réussi!</div>
            <div style={{ fontSize: "12px", marginTop: "5px" }}>
              {`${adjustmentsToExport.length} ajustements exportés dans "${filename}"`}
            </div>
            <div style={{ fontSize: "12px" }}>
              {`Filtres appliqués: ${filtersApplied.join(", ")}`}
            </div>
          </>
        ),
        key: "stockAdjustmentsExport",
        duration: 5,
      });
    } catch (error) {
      console.error("Erreur lors de l'export Excel des ajustements:", error);
      message.error({
        content: `Erreur lors de l'export Excel: ${
          error instanceof Error ? error.message : "Erreur inconnue"
        }`,
        key: "stockAdjustmentsExport",
      });
    } finally {
      setExportLoading(false);
    }
  };

  // Handle create/edit modal
  const openAdjustmentModal = useCallback((adjustmentId = null, seededItems = []) => {
    setEditingAdjustmentId(adjustmentId);
    setEditingValuation(null);
    form.resetFields();
    const permission = `Gestion Commerciale.Stock.GestionStock.Adjust.${adjustmentId ? "edit" : "create"}`;
    if (!hasPermission(permission)) return;
    const request = ++formRequestRef.current;
    const openedContext = contextRef.current;

    if (adjustmentId) {
      setDraftItems([]);
      // Edit mode - fetch adjustment details
      setLoading(true);
      axios
        .get(`/api/stock-adjustments/${adjustmentId}`, { headers: { "X-Company-Id": String(selectedCompany || "") } })
        .then((res) => {
          if (request !== formRequestRef.current || openedContext !== contextRef.current) return;
          const adjustmentData = res.data; // Pas de .adjustment d'après le code backend
          setEditingValuation(adjustmentData);
          form.setFieldsValue({
            product_id: adjustmentData.product_id,
            quantity: adjustmentData.quantity, // Le backend stocke déjà la quantité positive
            adjustment_type: adjustmentData.adjustment_type, // Le backend stocke déjà le type
            notes: adjustmentData.notes,
            reason_code: adjustmentData.reason_code || "unspecified",
            expected_valuation_event_id: adjustmentData.valuation_event_id || null,
          });
        })
        .catch((error) => {
          if (request === formRequestRef.current) {
            message.error("Impossible de charger les détails de l'ajustement");
            setModalVisible(false);
          }
        })
        .finally(() => {
          if (request === formRequestRef.current) setLoading(false);
        });
    } else {
      // Create mode - set defaults
      setDraftItems(seededItems);
      form.setFieldsValue({
        notes: "",
        reason_code: "inventory_difference",
      });
    }

    setModalVisible(true);
  }, [form, hasPermission, selectedCompany]);

  useEffect(() => {
    if (
      !selectedWarehouse ||
      !autoOpenCreate ||
      (initialProductId && products.length === 0) ||
      autoOpenHandledRef.current ||
      !hasPermission("Gestion Commerciale.Stock.GestionStock.Adjust.create")
    ) {
      return;
    }

    autoOpenHandledRef.current = true;
    const seededItems = buildAdjustmentDraftItems(initialItems, products);
    if (seededItems.length === 0 && initialProductId) {
      const directProduct = products.find(
        (product) => Number(product.id) === Number(initialProductId)
      );
      if (directProduct) {
        seededItems.push({
          key: String(directProduct.id),
          product_id: Number(directProduct.id),
          product_name: directProduct.name || directProduct.nom_produit,
          current_stock: Number(directProduct.current_stock) || 0,
          adjustment_type: "add",
          quantity: null,
        });
      }
    }
    openAdjustmentModal(null, seededItems);
  }, [
    autoOpenCreate,
    hasPermission,
    initialItems,
    initialProductId,
    openAdjustmentModal,
    products,
    selectedWarehouse,
  ]);

  const closeAdjustmentModal = () => {
    if (loading || submittingRef.current) return;
    formRequestRef.current += 1;
    const cancelledReplenishment =
      !editingAdjustmentId && Boolean(replenishmentContext);
    setModalVisible(false);
    setDraftItems([]);
    form.resetFields();
    if (cancelledReplenishment) {
      onReplenishmentCancelled?.();
    }
  };

  const addDraftProduct = (productId) => {
    if (resources.products.status !== "verified") return;
    const numericProductId = Number(productId);
    const product = products.find(
      (candidate) => Number(candidate.id) === numericProductId
    );
    if (!product) return;
    if (
      draftItems.some(
        (item) => Number(item.product_id) === numericProductId
      )
    ) {
      message.info("Ce produit est déjà présent dans l'ajustement.");
        return;
    }
    setDraftItems((current) => [
      ...current,
      {
        key: String(numericProductId),
        product_id: numericProductId,
        product_name: product.name || product.nom_produit,
        current_stock: Number(product.current_stock) || 0,
        adjustment_type: "add",
        quantity: null,
      },
    ]);
  };

  const updateDraftItem = (productId, field, value) => {
    setDraftItems((current) =>
      current.map((item) =>
        Number(item.product_id) === Number(productId)
          ? { ...item, [field]: value }
          : item
      )
    );
  };

  const removeDraftItem = (productId) => {
    setDraftItems((current) =>
      current.filter(
        (item) => Number(item.product_id) !== Number(productId)
      )
    );
  };

  // Handle form submission
  const handleSubmit = async (values) => {
    if (submittingRef.current || !hasPermission(`Gestion Commerciale.Stock.GestionStock.Adjust.${editingAdjustmentId ? "edit" : "create"}`)) return;
    submittingRef.current = true;
    const submittedContext = contextRef.current;
    const request = formRequestRef.current;
    const snapshot = JSON.stringify({ values: form.getFieldsValue(true), items: itemsRef.current });
    try {
      setLoading(true);
      const fresh = await resources.refresh();
      if (submittedContext !== contextRef.current || request !== formRequestRef.current ||
        snapshot !== JSON.stringify({ values: form.getFieldsValue(true), items: itemsRef.current })) {
        throw new Error("Le contexte ou la saisie a changé. Vérifiez l’ajustement avant de réessayer.");
      }
      if (!fresh.warehouses.some(row => String(row.id) === String(selectedWarehouse))) {
        throw new Error("L’établissement sélectionné n’est plus disponible.");
      }
      const rows = editingAdjustmentId ? [{ product_id: values.product_id }] : draftItems;
      for (const item of rows) {
        const product = fresh.products.find(row => String(row.id) === String(item.product_id));
        const stock = stockQuantity(product);
        if (!product || stock === null || (!editingAdjustmentId && item.adjustment_type === "subtract" && Number(item.quantity) > stock)) {
          throw new Error(`Vérifiez le stock de « ${item.product_name || product?.name || "ce produit"} ». La saisie est conservée.`);
        }
      }

      if (editingAdjustmentId) {
        const formData = {
          company_id: selectedCompany,
          warehouse_id: selectedWarehouse,
          product_id: values.product_id,
          quantity: values.quantity,
          adjustment_type: values.adjustment_type,
          notes: values.notes,
          reason_code: values.reason_code,
          expected_valuation_event_id: values.expected_valuation_event_id ?? null,
          created_by: user?.id,
        };
        await axios.put(
          `/api/stock-adjustments/${editingAdjustmentId}`,
          formData
        );
        message.success("Ajustement mis à jour avec succès");
      } else {
        const items = buildAdjustmentBatchPayload(draftItems);
        if (
          items.length === 0 ||
          items.some(
            (item) => !item.product_id || !item.quantity
          )
        ) {
          message.warning(
            "Ajoutez au moins un produit et renseignez une quantité positive pour chaque ligne."
          );
          return;
        }
        await axios.post("/api/stock-adjustments/batch", {
          company_id: selectedCompany,
          warehouse_id: selectedWarehouse,
          request_id:
            prefillRequestId || `manual-stock-adjustment-${Date.now()}`,
          notes: values.notes,
          reason_code: values.reason_code,
          items,
        });
        message.success(
          `${items.length} ajustement${items.length > 1 ? "s" : ""} créé${
            items.length > 1 ? "s" : ""
          } avec succès`
        );
      }

      void resources.invalidate();
      if (submittedContext !== contextRef.current) return;
      setModalVisible(false);
      setDraftItems([]);
      fetchAdjustments();
      if (!editingAdjustmentId && replenishmentContext) {
        onReplenishmentCompleted?.();
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.details || error.message ||
        "Erreur lors de l'enregistrement de l'ajustement";
      message.error(errorMessage);
    } finally {
      submittingRef.current = false;
      if (submittedContext === contextRef.current) setLoading(false);
    }
  };

  // Handle delete adjustment
  const handleDelete = async (adjustmentId, valuationEventId) => {
    try {
      setLoading(true);
      await axios.delete(`/api/stock-adjustments/${adjustmentId}`, {
        headers: { "X-Company-Id": String(selectedCompany || "") },
        data: { expected_valuation_event_id: valuationEventId ?? null },
      });
      void resources.invalidate();
      message.success("Ajustement supprimé avec succès");
      fetchAdjustments();
    } catch (error) {
      // ✅ AMÉLIORATION: Afficher le message détaillé du backend
      if (error.response?.data) {
        const errorData = error.response.data;

        // Afficher le message principal
        dialogApi.error({
          title: errorData.error || "Suppression impossible",
          content: (
            <div>
              <p style={{ marginBottom: '10px', fontWeight: 'bold' }}>
                {errorData.message}
              </p>

              {errorData.explication && (
                <p style={{ marginBottom: '10px' }}>
                  {errorData.explication}
                </p>
              )}

              {errorData.details && errorData.details.stock_actuel && (
                <div style={{
                  background: '#f5f5f5',
                  padding: '10px',
                  borderRadius: '4px',
                  marginBottom: '10px'
                }}>
                  <strong>Détails :</strong>
                  <ul style={{ marginTop: '5px', marginBottom: '0' }}>
                    {errorData.details.produit && <li>Produit : {errorData.details.produit}</li>}
                    <li>Stock actuel : {errorData.details.stock_actuel}</li>
                    <li>Ajustement : {errorData.details.ajustement}</li>
                    <li>Résultat : {errorData.details.stock_apres_suppression}</li>
                  </ul>
                </div>
              )}

              {errorData.que_faire && errorData.que_faire.length > 0 && (
                <div>
                  <strong>Que faire ?</strong>
                  <ul style={{ marginTop: '5px', marginBottom: '0', paddingLeft: '20px' }}>
                    {errorData.que_faire.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ),
          width: 600,
          okText: "Compris"
        });
      } else {
        // Fallback pour les autres erreurs
        message.error("Erreur lors de la suppression de l'ajustement");
      }
    } finally {
      setLoading(false);
    }
  };

  // Define columns for adjustments table
  const columns = [
    {
      title: "Date et heure",
      dataIndex: "created_at",
      key: "created_at",
      sorter: true,
      sortOrder: adjustmentSort.field === "created_at" ? adjustmentSort.order : null,
      render: (date) => moment(date).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "Produit",
      dataIndex: "product_name",
      key: "product_name",
      sorter: true,
      sortOrder: adjustmentSort.field === "product_name" ? adjustmentSort.order : null,
      render: value => <span className="stock-identity">{value}</span>,
    },
    {
      title: "Type",
      dataIndex: "adjustment_type",
      key: "adjustment_type",
      render: (adjustmentType) => (
        <Tag color={adjustmentType === "add" ? "green" : "red"} icon={adjustmentType === "add" ? <PlusCircleOutlined /> : <MinusCircleOutlined />}>
          {adjustmentType === "add" ? "Ajout" : "Retrait"}
        </Tag>
      ),
    },
    {
      title: "Quantité",
      dataIndex: "quantity",
      key: "quantity",
      render: (quantity, record) => (
        <span
          className={`stock-quantity stock-quantity-${record.adjustment_type}`}
        >
          {record.adjustment_type === "add" ? "+" : "-"}
          {formatNumber(quantity)} {record.unit_short_name || record.unit_name || ""}
        </span>
      ),
      align: "right",
    },
    { title: "Valeur du stock (+/−)", key: "stock_value_delta", align: "right", width: 190,
      render: (_, record) => <StockAdjustmentValuation adjustment={record} /> },
    { title: "Motif", dataIndex: "reason_code", key: "reason_code", render: adjustmentReasonLabel },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      ellipsis: true,
    },
    {
      title: "Créé par",
      dataIndex: "user_name",
      key: "user_name",
    },
    {
      title: "Actions", key: "actions", width: 80,
      render: (_, record) => <RowActionsMenu ariaLabel={`Actions de l’ajustement ${record.id}`} items={[
        hasPermission("Gestion Commerciale.Stock.GestionStock.Adjust.edit") && { key: "edit", label: "Modifier", icon: <EditOutlined />,
          onClick: () => openAdjustmentModal(record.id) },
        hasPermission("Gestion Commerciale.Stock.GestionStock.Adjust.delete") && { key: "delete", label: "Supprimer", icon: <DeleteOutlined />, danger: true,
          disabled: record.is_deletable === false, onClick: () => dialogApi.confirm({ title: "Supprimer cet ajustement ?",
            okText: "Supprimer", cancelText: "Annuler", okButtonProps: { danger: true }, onOk: () => handleDelete(record.id, record.valuation_event_id) }) },
      ].filter(Boolean)} />,
    },
  ];

  return (
    <div className="stock-workspace-pane" style={workspaceTheme} data-stock-theme={workspaceTheme["--stock-mode"]}>
      {dialogHolder}
      <StockPageHeader description="Consultez et suivez les corrections de stock.">
        {hasPermission("Gestion Commerciale.Stock.GestionStock.Adjust.view") && <Button icon={<FileExcelOutlined />} onClick={handleExportToExcel}
          disabled={!selectedWarehouse} loading={exportLoading}>Exporter sur Excel</Button>}
        {hasPermission("Gestion Commerciale.Stock.GestionStock.Adjust.create") && <Button type="primary" icon={<PlusOutlined />}
          onClick={() => openAdjustmentModal()} disabled={!selectedWarehouse}>Nouvel ajustement</Button>}
      </StockPageHeader>
      <StockSummary loading={loading} unavailable={!selectedWarehouse} items={[
        { label: "Total des ajustements", value: adjustmentSummary?.total, icon: <SwapOutlined /> },
        { label: "Ajouts au stock", value: adjustmentSummary?.additions, icon: <PlusCircleOutlined />, tone: "success" },
        { label: "Retraits du stock", value: adjustmentSummary?.withdrawals, icon: <MinusCircleOutlined />, tone: "danger" },
      ]} />
      <StockReadNotice error={listError} retry={fetchAdjustments} loading={loading} hasData={Boolean(adjustmentSummary || adjustments.length)} />
      <section className="stock-register">
        <div className="stock-register-heading"><div><h2>Ajustements de stock</h2><p>Historique des ajouts et retraits.</p></div></div>
        <StockFilters searchLabel="Rechercher dans les notes" placeholder="Mot-clé dans les notes…" value={adjustmentDraft} onChange={setAdjustmentDraft}
          onApply={() => { setAdjustmentFilters(adjustmentDraft); setProductFilter(adjustmentDraft.product || null); setDateRangeFilter(adjustmentDraft.period || null); setCurrentPage(1); }}
          onReset={() => { setAdjustmentDraft({}); setAdjustmentFilters({}); setProductFilter(null); setDateRangeFilter(null); setCurrentPage(1); }}>
          <StockRemoteSelect endpoint="/api/stock-adjustments" params={{ warehouse_id: selectedWarehouse }} kind="products" label="Produit"
            companyId={selectedCompany} warehouseId={selectedWarehouse} value={adjustmentDraft.product} placeholder="Tous les produits"
            onChange={product => setAdjustmentDraft({ ...adjustmentDraft, product })} />
          <StockFilterField label="Type d’ajustement"><Select aria-label="Type d’ajustement" allowClear value={adjustmentDraft.type} placeholder="Tous les types"
            options={[{ value: "add", label: "Ajout" }, { value: "subtract", label: "Retrait" }]}
            onChange={type => setAdjustmentDraft({ ...adjustmentDraft, type })} /></StockFilterField>
          <StockRemoteSelect endpoint="/api/stock-adjustments" params={{ warehouse_id: selectedWarehouse }} kind="actors" label="Créé par"
            companyId={selectedCompany} warehouseId={selectedWarehouse} value={adjustmentDraft.actor} placeholder="Tous les utilisateurs"
            onChange={actor => setAdjustmentDraft({ ...adjustmentDraft, actor })} />
        </StockFilters>
        <Table className="stock-workspace-table" dataSource={adjustments} columns={columns} rowKey="id" loading={loading} scroll={{ x: 1000 }}
          sortDirections={["descend", "ascend", "descend"]}
          onChange={(_, __, sorter) => {
            const field = ["created_at", "product_name"].includes(sorter.columnKey) ? sorter.columnKey : "created_at";
            const order = sorter.order || "descend";
            if (field !== adjustmentSort.field || order !== adjustmentSort.order) { setAdjustmentSort({ field, order }); setCurrentPage(1); }
          }}
          locale={{ emptyText: listError ? "Données indisponibles." : selectedWarehouse ? "Aucun ajustement pour ces filtres." : "Sélectionnez un établissement." }}
          pagination={{ current: currentPage, pageSize, total: totalItems, showSizeChanger: true,
            onChange: (page, size) => { setCurrentPage(page); setPageSize(size); },
            showTotal: (total, range) => `${range[0]}–${range[1]} sur ${total} ajustements` }} />
      </section>

      {editingAdjustmentId ? <Modal title="Modifier un ajustement de stock" open={modalVisible}
        onCancel={closeAdjustmentModal} footer={null} width={700}>
        <Form form={form} layout="vertical" onFinish={handleSubmit} disabled={loading}>
          <Form.Item name="expected_valuation_event_id" hidden><Input /></Form.Item>
          <p>Valeur enregistrée : <StockAdjustmentValuation adjustment={editingValuation} /></p>
          <Form.Item name="product_id" label="Produit" rules={[{ required: true, message: "Produit requis" }]}>
            <Select disabled loading={productsLoading} options={productOptions.flatOptions.map(option => ({ value: option.value, label: option.selectedLabel }))} />
          </Form.Item>
          <Row gutter={16}><Col xs={24} sm={12}>
            <Form.Item name="adjustment_type" label="Type d’ajustement" rules={[{ required: true, message: "Type requis" }]}>
              <Select options={[{ value: "add", label: "Ajout au stock (+)" }, { value: "subtract", label: "Retrait du stock (−)" }]} />
            </Form.Item></Col><Col xs={24} sm={12}>
            <Form.Item name="quantity" label="Quantité (absolue)" rules={[{ required: true, message: "Quantité requise" }, { type: "number", min: 0.01, message: "La quantité doit être positive" }]}>
              <InputNumber min={0.01} style={{ width: "100%" }} />
            </Form.Item></Col></Row>
          <Form.Item name="reason_code" label="Motif" rules={[{ required: true, message: "Choisissez un motif" }]}><Select options={STOCK_ADJUSTMENT_REASONS} /></Form.Item>
          <Form.Item name="notes" label="Raison / Notes"><TextArea rows={3} /></Form.Item>
          <p><Text type="secondary">Une correction de quantité sera valorisée à l’enregistrement. Modifier uniquement le motif ou les notes conserve le coût enregistré.</Text></p>
          <Row justify="end"><Space><Button onClick={closeAdjustmentModal}>Annuler</Button>
            <Button type="primary" htmlType="submit" loading={loading}>Mettre à jour</Button></Space></Row>
        </Form>
      </Modal> : <StockAdjustmentFormModal open={modalVisible} form={form} onSubmit={handleSubmit}
        onCancel={closeAdjustmentModal} loading={loading} items={draftItems} products={products}
        productOptions={productOptions.flatOptions} resources={resources} onAdd={addDraftProduct}
        onChange={updateDraftItem} onRemove={removeDraftItem} warehouseId={selectedWarehouse}
        matchesOption={matchesStockAdjustmentOption}
        canSubmit={hasPermission("Gestion Commerciale.Stock.GestionStock.Adjust.create") && draftItems.length > 0 &&
          !buildAdjustmentBatchPayload(draftItems).some(item => !item.product_id || !item.quantity)} />}

    </div>
  );
};

export { StockAdjustments, StockTransfers };
export default GestionStock;
